import { appNoOp, onError } from '@actions/app.action';
import {
  bookingGetBookingAfterPassengerUpdateSuccess,
  bookingTripDetailsInit
} from '@actions/booking.action';
import {
  checkInAutoAssignSeatsSuccess,
  checkInCheckInPassengers,
  checkInCheckInPassengersSuccess,
  checkInDocCheck,
  checkInDocCheckSuccess,
  checkInGetCheckInStatus,
  checkInGetCheckInStatusSuccess,
} from '@actions/check-in.action';
import { updatePassengersDocumentsSuccess } from '@actions/passenger.action';
import { inject, Injectable } from '@angular/core';
import { BaseError, ErrorType } from '@models/app.models';
import { CheckInPassengersRequestV3 } from '@models/requests/check-in-passengers-request.model';
import { DocumentTypeCode } from '@models/requests/passenger-update-request.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  selectBookingAreAllPassengersAssignedSeatsOnSegmentsInCheckinWindow,
  selectBookingCheckInPassengers,
  selectCanAnyJourneyCheckIn,
  selectPassengersList,
} from '@selectors/booking.selector';
import {
  selectIsBookPath,
  selectIsCheckInPath,
  selectIsModifyPath,
  selectIsTripsPath,
} from '@selectors/router.selector';
import { CheckInService } from '@services/api-clients/check-in.service';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { Observable, of } from 'rxjs';
import { mergeMap, switchMap, catchError, exhaustMap } from 'rxjs/operators';

@Injectable()
export class CheckInEffect {
  private readonly gbs = inject(GrowthBookService);
  constructor(
    private actions$: Actions,
    private store: Store,
    private checkInService: CheckInService,
  ) {}

  checkInPassengers$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(checkInCheckInPassengers),
      concatLatestFrom(() => this.store.select(selectBookingCheckInPassengers)),
      mergeMap(([action, checkInPassengers]) => {
        if (checkInPassengers) {
          const paxRequest = Object.keys(checkInPassengers)
            .filter((key) => checkInPassengers[key])
            .map((key) => ({ passengerKey: key }));

          return this.checkInService
            .checkInPassengers({
              passengers: paxRequest,
            } as CheckInPassengersRequestV3)
            .pipe(
              switchMap((result) => {
                return of(
                  checkInCheckInPassengersSuccess({
                    payload: result.data,
                    navigate: action.navigate,
                  }),
                );
              }),
              catchError(() => {
                return of(
                  onError({
                    error: new BaseError(ErrorType.CheckInPassengersFailure),
                  }),
                );
              }),
            );
        } else {
          return of(appNoOp());
        }
      }),
    );
  });

  tripDetailsGetCheckInStatus$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingTripDetailsInit),
      concatLatestFrom(() => this.store.select(selectCanAnyJourneyCheckIn)),
      mergeMap(([, canAnyJourneyCheckIn]) => {
        if (!canAnyJourneyCheckIn) {
          return of(appNoOp());
        }
        return of(checkInGetCheckInStatus());
      }),
    );
  });

  getCheckInStatus$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(checkInGetCheckInStatus),
      mergeMap(() => {
        return this.checkInService.getCheckInStatus().pipe(
          switchMap((result) => {
            return of(checkInGetCheckInStatusSuccess({ payload: result.data }));
          }),
          catchError(() => {
            return of(
              onError({
                error: new BaseError(ErrorType.GetCheckInStatusFailure),
              }),
            );
          }),
        );
      }),
    );
  });

  // TODO: trigger only if booking has any unassigned seats
  autoAssignSeats$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(updatePassengersDocumentsSuccess),
      concatLatestFrom(() => [
        this.store.select(
          selectBookingAreAllPassengersAssignedSeatsOnSegmentsInCheckinWindow,
        ),
      ]),
      mergeMap(([, areAllPaxAssignedSeatsOnSegmentsInCheckinWindow]) => {
        if (areAllPaxAssignedSeatsOnSegmentsInCheckinWindow) {
          return of(checkInAutoAssignSeatsSuccess());
        }
        return this.checkInService.autoAssignSeats().pipe(
          switchMap(() => {
            return of(checkInAutoAssignSeatsSuccess());
          }),
          catchError(() => {
            return of(
              onError({
                error: new BaseError(ErrorType.AutoAssignSeatsFailure),
              }),
            );
          }),
        );
      }),
    );
  });

  checkInPassengersDocCheck$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(checkInDocCheck),
      concatLatestFrom(() => [
        this.store.select(selectBookingCheckInPassengers),
        this.store.select(selectPassengersList),
        this.store.select(selectIsBookPath),
        this.store.select(selectIsModifyPath),
        this.store.select(selectIsTripsPath),
        this.store.select(selectIsCheckInPath),
      ]),
      mergeMap(
        ([
          ,
          checkInPassengers,
          passengers,
          isBookPath,
          isModifyPath,
          isTripsPath,
          isCheckinPath,
        ]) => {
          if (
            (isBookPath &&
              !this.gbs.isVariantOfExperimentEnabled(
                'feat-002814-doccheck-popup-booking-flow',
                'b',
              )) ||
            ((isModifyPath || isTripsPath) &&
              !this.gbs.isVariantOfExperimentEnabled(
                'feat-002815-doccheck-popup-MMB',
                'b',
              )) ||
            (isCheckinPath &&
              !this.gbs.isVariantOfExperimentEnabled(
                'feat-002688-online-check-in',
                true,
              ))
          ) {
            return of(appNoOp());
          }

          const filterPassengersWithPassport = passengers?.filter((passenger) =>
            passenger.travelDocuments?.some(
              (doc) => doc.documentTypeCode === DocumentTypeCode.Passport,
            ),
          );

          const transformedPassengersToCheckinPassengers =
            filterPassengersWithPassport?.reduce((acc, passenger) => {
              acc[passenger.passengerKey] = true;
              return acc;
            }, {});

          let passengersToCheckinPassengers: string[] = [];

          if (checkInPassengers) {
            passengersToCheckinPassengers = Object.keys(
              checkInPassengers,
            ).filter((key) => checkInPassengers[key]);
          }

          if (transformedPassengersToCheckinPassengers) {
            passengersToCheckinPassengers = Object.keys(
              transformedPassengersToCheckinPassengers,
            );
          }

          if (passengersToCheckinPassengers.length > 0) {
            const paxRequest = passengersToCheckinPassengers.map((key) => ({
              passengerKey: key,
            }));

            return this.checkInService
              .docCheck({ passengers: paxRequest })
              .pipe(
                switchMap((result) => {
                  return of(checkInDocCheckSuccess({ payload: result.data }));
                }),
                catchError(() => {
                  return of(
                    onError({
                      error: new BaseError(ErrorType.CheckInDocCheckFailure),
                    }),
                  );
                }),
              );
          } else {
            return of(appNoOp());
          }
        },
      ),
    );
  });

  checkInDocCheckAfterUpdateTripsPassengersSuccess$: Observable<Action> =
    createEffect(() => {
      return this.actions$.pipe(
        ofType(
          bookingGetBookingAfterPassengerUpdateSuccess,
        ),
        exhaustMap(() => {
          return of(checkInDocCheck());
        }),
      );
    });
}
